import { createTrackingUtils } from "../index";
import { UIBrandedFareInfo } from "@flights/types";
import { getFeaturesToRender } from "branded-fares/utils/features";
import { filterByAvailability, isCancellableFeature, isFlexibleFeature } from "@flights/branded-fares";

const utils = createTrackingUtils({
  name: "flights_apex_web_clarify_flex_features",
  stages: {
    main: 1,
    mdot: 2,
    ddot: 3,
    branded_fares: 4,
    search_results: 5,
    check_and_pay: 6,
    gte_1_sellable_or_included_change: 7,
    gte_2_sellable_or_included_change: 8,
    gte_1_sellable_refundable: 9
  },
  goals: {},
  defaultStage: 1
});

type WithBrandedFareInfo = {
  brandedFareInfo: UIBrandedFareInfo;
};

const trackStages = (
  isMobile: boolean,
  page: "branded_fares" | "search_results" | "check_and_pay",
  brandedFareOffers: WithBrandedFareInfo[]
) => {
  if (!brandedFareOffers.some(({ brandedFareInfo }) => hasFlexibility(brandedFareInfo))) return;

  brandedFareOffers.forEach(({ brandedFareInfo }) => {
    /**
     * It's important that we use .featuresList instead of using .features and .sellableFeatures for this tracking.
     * This is because .featuresList has the unfiltered features in the variant of this exp.
     */
    const includedFeatures = filterByAvailability(brandedFareInfo.featuresList, "INCLUDED");
    const sellableFeatures = filterByAvailability(brandedFareInfo.featuresList, "SELLABLE");

    const includedSellableFeatures = [...includedFeatures, ...sellableFeatures];
    const changeFeatureCount = includedSellableFeatures.filter((f) => isFlexibleFeature(f.featureName)).length;
    const hasSellableCancellable = sellableFeatures.some((f) => isCancellableFeature(f.featureName));

    if (changeFeatureCount >= 1) utils.stages.gte_1_sellable_or_included_change();
    if (changeFeatureCount >= 2) utils.stages.gte_2_sellable_or_included_change();
    if (hasSellableCancellable) utils.stages.gte_1_sellable_refundable();
  });

  utils.stages.main();

  if (isMobile) utils.stages.mdot();
  if (!isMobile) utils.stages.ddot();
  if (page === "branded_fares") utils.stages.branded_fares();
  if (page === "search_results") utils.stages.search_results();
  if (page === "check_and_pay") utils.stages.check_and_pay();
};

const hasFlexibility = (brandedFareInfo: UIBrandedFareInfo) => {
  const { includedFeatures, nonIncludedFeatures } = getFeaturesToRender(brandedFareInfo);
  return [...includedFeatures, ...nonIncludedFeatures].some((feature) => feature.group === "FLEXIBILITY");
};

export default { ...utils, trackStages };
